import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  PhotoCamera as CameraIcon,
  MenuBook as BookIcon,
  Restaurant as RestaurantIcon,
  Chat as ChatIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

export interface WelcomeTourProps {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const WelcomeTour: React.FC<WelcomeTourProps> = ({ open, onComplete, onSkip }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = 5;

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const pageVariants = {
    enter: (direction: number) => ({
      rotateY: direction > 0 ? 90 : -90,
      opacity: 0,
    }),
    center: {
      rotateY: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      rotateY: direction > 0 ? -90 : 90,
      opacity: 0,
    }),
  };

  const pages = [
    {
      icon: <Box
        component="img"
        src="/assets/images/otto/otto_welcome.svg"
        alt="Otto"
        sx={{
          width: { xs: 180, md: 220 },
          height: { xs: 180, md: 220 },
          mb: 2,
        }}
      />,
      title: 'Meet Otto, Your AI Sous Chef',
      content: "I'm here to help you capture, organize, and perfect your recipes. Let me show you around your new kitchen!",
    },
    {
      icon: <CameraIcon sx={{ fontSize: { xs: 80, md: 100 }, mb: 3, color: '#FF6B35' }} />,
      title: 'Snap Any Recipe',
      content: 'Take a photo of recipes from cookbooks, magazines, or handwritten cards. I\'ll extract ingredients and steps automatically.',
      subtitle: <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mt: 2,
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src="/assets/images/greentokens.png"
          alt="Green Tokens"
          sx={{ width: 20, height: 20 }}
        />
        <Typography
          variant="caption"
          sx={{
            fontFamily: '"Crimson Text", serif',
            fontSize: '0.9rem',
            color: 'rgba(139, 69, 19, 0.7)',
          }}
        >
          2 tokens per recipe extraction
        </Typography>
      </Box>,
    },
    {
      icon: <BookIcon sx={{ fontSize: { xs: 80, md: 100 }, mb: 3, color: '#4A7C59' }} />,
      title: 'Your Personal Cookbook',
      content: 'Search, organize, and favorite your recipes. Everything syncs securely to your account.',
    },
    {
      icon: <RestaurantIcon sx={{ fontSize: { xs: 80, md: 100 }, mb: 3, color: '#FF8A65' }} />,
      title: 'Cook with Confidence',
      content: 'Get step-by-step guidance, timers, ingredient substitutions, and voice instructions while you cook.',
    },
    {
      icon: <ChatIcon sx={{ fontSize: { xs: 80, md: 100 }, mb: 3, color: '#6B9B7B' }} />,
      title: 'When in Doubt, Ask Otto',
      content: 'Stuck? Not sure? Just ask! I can help with substitutions, technique questions, scaling recipes, and troubleshooting.',
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onSkip}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(46, 33, 23, 0.98) 0%, rgba(32, 22, 16, 0.98) 100%)'
            : '#FFFEF8',
          backgroundImage: theme.palette.mode === 'dark'
            ? 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 248, 240, 0.02) 2px, rgba(255, 248, 240, 0.02) 4px)'
            : 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(139, 69, 19, 0.02) 2px, rgba(139, 69, 19, 0.02) 4px)',
          border: theme.palette.mode === 'dark'
            ? '3px solid rgba(255, 138, 101, 0.3)'
            : '3px solid rgba(139, 69, 19, 0.2)',
          minHeight: isMobile ? '100vh' : 500,
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      {/* Skip Button */}
      <IconButton
        onClick={onSkip}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 10,
          color: theme.palette.mode === 'dark'
            ? 'rgba(255, 248, 240, 0.6)'
            : 'rgba(139, 69, 19, 0.6)',
          '&:hover': {
            color: theme.palette.mode === 'dark'
              ? 'rgba(255, 248, 240, 0.9)'
              : 'rgba(139, 69, 19, 0.9)',
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 248, 240, 0.1)'
              : 'rgba(139, 69, 19, 0.1)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: { xs: 3, md: 4 },
          minHeight: isMobile ? '100vh' : 500,
          position: 'relative',
          perspective: '1000px',
        }}
      >
        {/* Page Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: 600,
            textAlign: 'center',
            px: { xs: 2, md: 4 },
          }}
        >
          <AnimatePresence mode="wait" custom={1}>
            <motion.div
              key={currentPage}
              custom={1}
              variants={pageVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                rotateY: { type: 'spring', stiffness: 100, damping: 20 },
                opacity: { duration: 0.3 },
              }}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {/* Icon/Image */}
              {pages[currentPage].icon}

              {/* Title */}
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 700,
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 248, 240, 0.9)'
                    : 'rgba(139, 69, 19, 0.9)',
                  mb: 3,
                  fontSize: { xs: '1.75rem', md: '2.125rem' },
                }}
              >
                {pages[currentPage].title}
              </Typography>

              {/* Content */}
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"Crimson Text", serif',
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 248, 240, 0.8)'
                    : 'rgba(139, 69, 19, 0.8)',
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  lineHeight: 1.6,
                  maxWidth: 500,
                }}
              >
                {pages[currentPage].content}
              </Typography>

              {/* Subtitle (if exists) */}
              {pages[currentPage].subtitle}
            </motion.div>
          </AnimatePresence>
        </Box>

        {/* Progress Dots */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            my: 3,
          }}
        >
          {Array.from({ length: totalPages }).map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: index === currentPage
                  ? (theme.palette.mode === 'dark' ? '#FF8A65' : '#FF6B35')
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 248, 240, 0.3)'
                    : 'rgba(139, 69, 19, 0.3)'),
                transition: 'all 0.3s ease',
                transform: index === currentPage ? 'scale(1.3)' : 'scale(1)',
              }}
            />
          ))}
        </Box>

        {/* Navigation Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: 500,
            gap: 2,
          }}
        >
          <Button
            onClick={handleBack}
            disabled={currentPage === 0}
            startIcon={<BackIcon />}
            sx={{
              fontFamily: '"Crimson Text", serif',
              fontSize: '1rem',
              textTransform: 'none',
              color: theme.palette.mode === 'dark'
                ? 'rgba(255, 248, 240, 0.7)'
                : 'rgba(139, 69, 19, 0.7)',
              '&:disabled': {
                color: theme.palette.mode === 'dark'
                  ? 'rgba(255, 248, 240, 0.3)'
                  : 'rgba(139, 69, 19, 0.3)',
              },
            }}
          >
            Back
          </Button>

          <Button
            onClick={handleNext}
            endIcon={currentPage < totalPages - 1 ? <NextIcon /> : null}
            variant="contained"
            sx={{
              fontFamily: '"Crimson Text", serif',
              fontSize: '1rem',
              textTransform: 'none',
              px: 4,
              py: 1,
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 138, 101, 0.3)'
                : 'rgba(255, 107, 53, 0.9)',
              color: '#FFFEF8',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 138, 101, 0.5)'
                  : 'rgba(255, 107, 53, 1)',
              },
            }}
          >
            {currentPage < totalPages - 1 ? 'Next' : "Let's Get Cooking!"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeTour;
